"use client";

import CaseStudyMeta from "@/components/common/seo/case-study-meta";
import { Button } from "@/components/ui/button";
import { caseStudies } from "@/lib/dto/case-studies/case-studies";
import { BannerBgVector, DailyStatusUpdates, ImgCozmoAgent, ImgCozmoHome, ImgCozmoPlanFinderToolHero, ImgCozmoScreenshotFilter, ImgCozmoSubmitPolicy1, ImgCozmoSubmitPolicy2, ImgCozmoUpdateLead, ImgLakhanPurohit, LogoDvekrestWhite, TrelloCard } from "@/lib/images";
import Image from "next/image";
import Link from "next/link";

const CozmoPlanFinderToolCaseStudy = () => {
  // Find the case study data for this page
  const caseStudy = caseStudies.find(cs => cs.link === "/case-studies/ai-based-insurance-plan-finder-solution");

  return (
    <>
      {caseStudy && <CaseStudyMeta caseStudy={caseStudy} />}
      <main className="case-study pt-[calc(100vh-56rem)] ai-based-insurance-plan-finder-solution">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-50 via-white to-blue-50 pt-32 sm:pt-16 md:pt-20 lg:pt-32 pb-12 sm:pb-16 md:pb-20 lg:pb-32 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
              {/* Left Column - Content */}
              <div className="space-y-6 sm:space-y-8 lg:space-y-10">
                {/* Main Headline */}
                <div className="space-y-3 sm:space-y-4">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight">
                    Cozmo Plan Finder Tool
                  </h1>

                  <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl leading-relaxed">
                    How we revolutionized insurance plan discovery with AI, reducing search time by 90% and boosting sales by 50% for Cozmo Health.
                  </p>
                </div>

                {/* CTA Button */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-base h-12 sm:h-14"
                    onClick={() => {
                      const element = document.querySelector('.project-overview');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Explore Case Study
                  </Button>
                </div>
              </div>

              {/* Right Column - Hero Image */}
              <div className="relative order-first lg:order-last">
                <Image
                  src={ImgCozmoPlanFinderToolHero}
                  alt="AI Plan Finder Tool Interface"
                  className="w-full h-auto rounded-xl sm:rounded-2xl shadow-lg sm:shadow-2xl"
                  priority
                />
              </div>
            </div>
          </div>
        </section>

        {/* Project Overview Section */}
        <section className="project-overview items-start justify-start bg-gray-50 py-8 sm:py-12 md:py-16 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text mb-6 sm:mb-8 md:mb-12">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 md:mb-6 leading-tight">
                AI-powered plan finder tool that simplifies insurance selection,
                automates comparisons, provides personalized recommendations,
                and enhances user experience through intelligent matching.
              </h1>
            </div>

            {/* Team Member */}
            <div className="flex items-start justify-start mb-8 sm:mb-12 md:mb-16">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-full overflow-hidden">
                  <Image
                    src={ImgLakhanPurohit}
                    alt="Lakhan Purohit"
                    className="w-full h-full object-cover"
                    width={64}
                    height={64}
                  />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">Lakhan Purohit</h3>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 italic">Chief Technology Officer (Devkrest)</p>
                </div>
              </div>
            </div>

            {/* Three Column Layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 items-start">
              {/* Mission */}
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Mission</h2>
                <p className="text-xs sm:text-sm md:text-base text-gray-500 leading-relaxed">
                  Simplifying insurance plan selection, enhancing user decision-making,
                  automating comparisons, providing recommendations, and
                  driving user engagement efficiently.
                </p>
              </div>

              {/* Vision */}
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Vision</h2>
                <p className="text-xs sm:text-sm md:text-base text-gray-500 leading-relaxed">
                  To revolutionize insurance plan discovery,
                  streamline selection processes, and drive
                  growth through innovative, user-centric solutions.
                </p>
              </div>

              {/* Challenges */}
              <div className="space-y-2 sm:space-y-3 md:space-y-4 sm:col-span-2 lg:col-span-1">
                <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Challenges</h2>
                <p className="text-xs sm:text-sm md:text-base text-gray-500 leading-relaxed">
                  Complex plan structures, user confusion, comparison
                  difficulties, and security requirements, and inconsistent
                  user experience across platforms.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Problem & Solution Section */}
        <section className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 relative bg-black">
          <div className="max-w-6xl mx-auto">
            {/* Header with Context Chip */}
            <div className="mb-8 sm:mb-12 md:mb-16">
              {/* Context Chip */}
              <div className="mb-4 sm:mb-6 md:mb-8">
                <div className="inline-flex px-3 sm:px-4 py-2 border border-white rounded-full text-white text-xs sm:text-sm font-medium">
                  About Project
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 lg:gap-20">
              {/* First Column: Problem and Solution stacked */}
              <div className="space-y-8 sm:space-y-12 md:space-y-16">
                {/* The Problem */}
                <div className="space-y-4 sm:space-y-6 md:space-y-8">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-yellow-100 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-lg sm:text-xl md:text-2xl">🤔</span>
                    </div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white">
                      The Problem
                    </h2>
                  </div>

                  <p className="text-xs sm:text-sm md:text-base lg:text-lg text-slate-200 leading-relaxed">
                    Insurance agents were spending 6-10 minutes manually searching for plans
                    across different carriers, causing significant customer wait times and
                    reduced sales efficiency. The manual process was time-consuming, error-prone,
                    and didn't account for company commission priorities or regional variations
                    in plan availability.
                  </p>
                </div>

                {/* The Solution */}
                <div className="space-y-4 sm:space-y-6 md:space-y-8">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-lg sm:text-xl md:text-2xl">💡</span>
                    </div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white">
                      The Solution
                    </h2>
                  </div>

                  <p className="text-xs sm:text-sm md:text-base lg:text-lg text-slate-200 leading-relaxed">
                    We developed an AI-powered Plan Finder Tool that analyzes customer data
                    and finds the best plans from the CMS marketplace based on company
                    commission priorities and regional variations. The tool provides intelligent
                    filtering options and a "Show Best Plans" feature that displays optimal
                    $0 plans from each unique carrier. This reduced plan search time from
                    6-10 minutes to just 6 seconds, cutting customer wait time by 80% and
                    boosting Cozmo Health sales by nearly 50%.
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop: Overlapping Image Layout */}
            <div className="hidden lg:block">
              {/* Blue Line - Separated from padding */}
              <div className="absolute right-1/8 top-0 bottom-0 w-30 bg-white opacity-10"></div>
              {/* Agent Image */}
              <div className="flex justify-center mb-8">
                <div className="absolute right-1/16 top-1/4 bottom-0">
                  <Image
                    src={ImgCozmoAgent}
                    alt="Insurance Agent"
                    className="h-100 w-auto object-contain"
                    priority
                  />
                </div>
              </div>
              {/* Right Side Vertical Line */}
              <div className="absolute right-0 top-0 bottom-0 w-1 bg-gray-600"></div>
            </div>

            {/* Mobile/Tablet Agent Image - Stacked below content */}
            <div className="lg:hidden mt-8 sm:mt-12 md:mt-16">
              <div className="relative">
                <Image
                  src={ImgCozmoAgent}
                  alt="Insurance Agent"
                  className="w-full max-w-md mx-auto h-auto object-contain"
                  priority
                />
              </div>
            </div>
          </div>
        </section>

        {/* Survey Results Section */}
        <section className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8 sm:mb-12 md:mb-16">
              {/* Context Chip */}
              <div className="mb-4 sm:mb-6 md:mb-8">
                <div className="inline-flex px-3 sm:px-4 py-2 border-1 border-gray-300 rounded-full text-gray-700 text-xs sm:text-sm font-medium">
                  Research
                </div>
              </div>

              {/* Creative Title */}
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 md:mb-8">
                Research & User Insights
              </h2>

              <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed max-w-4xl">
                Understanding user pain points was crucial for developing the AI Plan Finder Tool.
                We conducted comprehensive research with insurance agents and customers to identify
                key challenges in plan selection and expectations for an automated solution.
                Our research involved 156 insurance agents and 89 customers across different states.
              </p>
            </div>

            {/* Statistics Cards */}
            <div className="mb-8 sm:mb-12 md:mb-16">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-4 sm:mb-6 md:mb-8">Research Participants</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                <div className="bg-blue-100 rounded-xl p-3 sm:p-4 md:p-6">
                  <div className="flex items-center justify-start space-x-2 sm:space-x-3 mb-2">
                    <div className="text-xl sm:text-2xl md:text-3xl">👋</div>
                    <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">168</div>
                  </div>
                  <div className="text-xs sm:text-sm md:text-base text-gray-700 font-medium text-left">Responders</div>
                </div>
                <div className="bg-gray-100 rounded-xl p-3 sm:p-4 md:p-6">
                  <div className="flex items-center justify-start space-x-2 sm:space-x-3 mb-2">
                    <div className="text-xl sm:text-2xl md:text-3xl">👨‍💼</div>
                    <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">15</div>
                  </div>
                  <div className="text-xs sm:text-sm md:text-base text-gray-700 font-medium text-left">Insurance Agents</div>
                </div>
                <div className="bg-gray-100 rounded-xl p-3 sm:p-4 md:p-6 sm:col-span-2 md:col-span-1">
                  <div className="flex items-center justify-start space-x-2 sm:space-x-3 mb-2">
                    <div className="text-xl sm:text-2xl md:text-3xl">👥</div>
                    <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">100+</div>
                  </div>
                  <div className="text-xs sm:text-sm md:text-base text-gray-700 font-medium text-left">Customers</div>
                </div>
              </div>
            </div>

            {/* User Expectations */}
            <div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-4 sm:mb-6 md:mb-8">What do agents expect from an AI Plan Finder Tool?</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                {/* Left Column */}
                <div className="bg-gray-100 rounded-xl p-4 sm:p-6 md:p-8">
                  <div className="space-y-3 sm:space-y-4 md:space-y-6">
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm md:text-sm text-gray-700 font-medium">Speed and Efficiency</span>
                      <span className="text-xs sm:text-sm md:text-base text-gray-900 font-bold">92%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 h-2 sm:h-3 rounded-full" style={{ width: '92%' }}></div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm md:text-sm text-gray-700 font-medium">Commission Optimization</span>
                      <span className="text-xs sm:text-sm md:text-base text-gray-900 font-bold">87%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 h-2 sm:h-3 rounded-full" style={{ width: '92%' }}></div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm md:text-sm text-gray-700 font-medium">Multi-Carrier Support</span>
                      <span className="text-xs sm:text-sm md:text-base text-gray-900 font-bold">84%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 h-2 sm:h-3 rounded-full" style={{ width: '92%' }}></div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm md:text-sm text-gray-700 font-medium">Regional Plan Matching</span>
                      <span className="text-xs sm:text-sm md:text-base text-gray-900 font-bold">79%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 h-2 sm:h-3 rounded-full" style={{ width: '92%' }}></div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="bg-gray-100 rounded-xl p-4 sm:p-6 md:p-8">
                  <div className="space-y-3 sm:space-y-4 md:space-y-6">
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm md:text-sm text-gray-700 font-medium">Customer Satisfaction</span>
                      <span className="text-xs sm:text-sm md:text-base text-gray-900 font-bold">89%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 h-2 sm:h-3 rounded-full" style={{ width: '92%' }}></div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm md:text-sm text-gray-700 font-medium">Automated Comparisons</span>
                      <span className="text-xs sm:text-sm md:text-base text-gray-900 font-bold">91%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 h-2 sm:h-3 rounded-full" style={{ width: '92%' }}></div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm md:text-sm text-gray-700 font-medium">Best Plan Recommendations</span>
                      <span className="text-xs sm:text-sm md:text-base text-gray-900 font-bold">85%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 h-2 sm:h-3 rounded-full" style={{ width: '92%' }}></div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm md:text-sm text-gray-700 font-medium">Reduced Wait Times</span>
                      <span className="text-xs sm:text-sm md:text-base text-gray-900 font-bold">94%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-700 h-2 sm:h-3 rounded-full" style={{ width: '92%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Glass Frosted Problems Section */}
        <section className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
          <div className="max-w-6xl mx-auto">
            {/* Context Chip */}
            <div className="mb-4 sm:mb-6 md:mb-8">
              <div className="inline-flex px-3 sm:px-4 py-2 border-1 border-gray-300 rounded-full text-gray-700 text-xs sm:text-sm font-medium">
                Pain Points
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6 sm:mb-8 md:mb-12">
              Without AI Plan Finder
            </h2>

            {/* Jumbled Glass Frosted Chips */}
            <div className="relative">
              {/* Background decorative elements */}
              <div className="absolute top-10 left-10 w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-blue-200 rounded-full opacity-20 blur-sm"></div>
              <div className="absolute top-32 right-20 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-purple-200 rounded-full opacity-30 blur-sm"></div>
              <div className="absolute bottom-20 left-1/4 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-green-200 rounded-full opacity-25 blur-sm"></div>

              {/* Glass Frosted Chips Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 relative z-10">
                {/* Chip 1 */}
                <div className="transform rotate-1 sm:rotate-2 hover:rotate-0 transition-transform duration-300">
                  <div className="backdrop-blur-sm bg-white/30 border border-white/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-red-600 text-xs sm:text-sm md:text-lg">⏰</span>
                      </div>
                      <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800">6-10 Minutes</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">Manual plan search time per customer</p>
                  </div>
                </div>

                {/* Chip 2 */}
                <div className="transform -rotate-1 hover:rotate-0 transition-transform duration-300">
                  <div className="backdrop-blur-sm bg-white/30 border border-white/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-orange-100 rounded-full flex items-center justify-center">
                        <span className="text-orange-600 text-xs sm:text-sm md:text-lg">😤</span>
                      </div>
                      <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800">Frustrated Customers</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">Long wait times causing dissatisfaction</p>
                  </div>
                </div>

                {/* Chip 3 */}
                <div className="transform rotate-2 sm:rotate-3 hover:rotate-0 transition-transform duration-300">
                  <div className="backdrop-blur-sm bg-white/30 border border-white/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <span className="text-yellow-600 text-xs sm:text-sm md:text-lg">💸</span>
                      </div>
                      <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800">Lost Revenue</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">Missed opportunities due to slow process</p>
                  </div>
                </div>

                {/* Chip 4 */}
                <div className="transform -rotate-1 sm:-rotate-2 hover:rotate-0 transition-transform duration-300">
                  <div className="backdrop-blur-sm bg-white/30 border border-white/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 text-xs sm:text-sm md:text-lg">🔍</span>
                      </div>
                      <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800">Manual Search</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">Scrolling through endless plan lists</p>
                  </div>
                </div>

                {/* Chip 5 */}
                <div className="transform rotate-1 hover:rotate-0 transition-transform duration-300">
                  <div className="backdrop-blur-sm bg-white/30 border border-white/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-xs sm:text-sm md:text-lg">📊</span>
                      </div>
                      <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800">No Optimization</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">Missing commission priority insights</p>
                  </div>
                </div>

                {/* Chip 6 */}
                <div className="transform -rotate-2 sm:-rotate-3 hover:rotate-0 transition-transform duration-300">
                  <div className="backdrop-blur-sm bg-white/30 border border-white/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-xs sm:text-sm md:text-lg">🌍</span>
                      </div>
                      <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800">Regional Confusion</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">Unaware of state-specific plan availability</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* In-depth Interview Section */}
        <section className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 bg-black">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8 sm:mb-12 md:mb-16">
              {/* Context Chip */}
              <div className="mb-4 sm:mb-6 md:mb-8">
                <div className="inline-flex px-3 sm:px-4 py-2 border border-white rounded-full text-white text-xs sm:text-sm font-medium">
                  Interview
                </div>
              </div>

              {/* Title */}
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 sm:mb-6 md:mb-8">
                In-depth Interview
              </h2>

              <p className="text-xs sm:text-sm md:text-base lg:text-lg text-white leading-relaxed max-w-4xl">
                Based on the survey results, we selected 15 insurance agents for in-depth interviews.
                These interviews provided a comprehensive understanding of the challenges they faced in
                plan selection, their expectations from automation tools, and their overall workflow patterns.
              </p>
            </div>

            {/* Interview Questions */}
            <div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-4 sm:mb-6 md:mb-8">Interview Questions</h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                {/* Left Column */}
                <div className="space-y-3 sm:space-y-4 md:space-y-6">
                  <div className="bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6">
                    <p className="text-white text-xs sm:text-sm md:text-sm">How long does it typically take you to find the right plan for a customer?</p>
                  </div>

                  <div className="bg-gradient-to-r from-blue-800 to-blue-600 rounded-xl p-3 sm:p-4 md:p-6 transform -rotate-1">
                    <p className="text-white text-xs sm:text-sm md:text-sm">What are the biggest pain points in your current plan search process?</p>
                  </div>

                  <div className="bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6">
                    <p className="text-white text-xs sm:text-sm md:text-sm">How do you currently handle commission optimization across different carriers?</p>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-3 sm:space-y-4 md:space-y-6">
                  <div className="bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6">
                    <p className="text-white text-xs sm:text-sm md:text-sm">Which factors most significantly affect your plan selection decisions?</p>
                  </div>

                  <div className="bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6">
                    <p className="text-white text-xs sm:text-sm md:text-sm">What features would you expect from an AI-powered plan finder tool?</p>
                  </div>

                  <div className="bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6">
                    <p className="text-white text-xs sm:text-sm md:text-sm">How important is regional plan availability in your decision-making process?</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Project Timeline Section */}
        <section className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8 sm:mb-12 md:mb-16">
              {/* Context Chip */}
              <div className="mb-4 sm:mb-6 md:mb-8">
                <div className="inline-flex px-3 sm:px-4 py-2 border-1 border-gray-300 rounded-full text-gray-700 text-xs sm:text-sm font-medium">
                  Project Timeline
                </div>
              </div>

              {/* Title */}
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 md:mb-8">
                Plan in Action
              </h2>

              <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed max-w-4xl">
                Our development process followed a structured approach, from initial research to final deployment.
                Each phase was carefully planned and executed to ensure the AI Plan Finder Tool met all requirements.
              </p>
            </div>

            {/* Modern Trello-style Kanban Board */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
              {/* Research Column */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg border border-blue-200">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4 md:mb-6">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white text-xs sm:text-sm font-bold">1</span>
                  </div>
                  <h3 className="text-sm sm:text-lg md:text-xl font-bold text-gray-900">Research</h3>
                </div>
                <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  {/* Task Card 1 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">Survey Analysis</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">Conducted survey with 168 insurance agents</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 1</span>
                    </div>
                  </div>

                  {/* Task Card 2 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">User Interviews</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">In-depth interviews with 15 agents</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 2</span>
                    </div>
                  </div>

                  {/* Task Card 3 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">Pain Point Analysis</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">Identified key challenges and requirements</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 2</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Design Column */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg border border-purple-200">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4 md:mb-6">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white text-xs sm:text-sm font-bold">2</span>
                  </div>
                  <h3 className="text-sm sm:text-lg md:text-xl font-bold text-gray-900">Design</h3>
                </div>
                <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  {/* Task Card 1 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">UI/UX Wireframes</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">Created detailed wireframes and mockups</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 3</span>
                    </div>
                  </div>

                  {/* Task Card 2 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">User Flow Mapping</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">Designed optimal user journey and interactions</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 3</span>
                    </div>
                  </div>

                  {/* Task Card 3 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">Prototype Creation</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">Built interactive prototype for testing</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 4</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Development Column */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg border border-green-200">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4 md:mb-6">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white text-xs sm:text-sm font-bold">3</span>
                  </div>
                  <h3 className="text-sm sm:text-lg md:text-xl font-bold text-gray-900">Development</h3>
                </div>
                <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  {/* Task Card 1 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">AI Algorithm Build</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">Developed intelligent plan matching algorithm</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 5</span>
                    </div>
                  </div>

                  {/* Task Card 2 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">CMS Integration</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">Integrated with CMS marketplace APIs</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 6</span>
                    </div>
                  </div>

                  {/* Task Card 3 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">API Development</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">Built RESTful APIs for data exchange</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 7-8</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Launch Column */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg border border-orange-200">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4 md:mb-6">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white text-xs sm:text-sm font-bold">4</span>
                  </div>
                  <h3 className="text-sm sm:text-lg md:text-xl font-bold text-gray-900">Launch</h3>
                </div>
                <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  {/* Task Card 1 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">Beta Testing</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">Conducted comprehensive beta testing</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 9</span>
                    </div>
                  </div>

                  {/* Task Card 2 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">Performance Optimization</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">Optimized for speed and efficiency</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 9</span>
                    </div>
                  </div>

                  {/* Task Card 3 */}
                  <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-5 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-900">Production Deployment</h4>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 sm:mb-3 md:mb-4 leading-relaxed">Successfully launched to production</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full font-medium">Completed</span>
                      <span className="text-xs text-gray-500 font-medium">Week 10</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Interface Showcase Section */}
        <section className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8 sm:mb-12 md:mb-16">
              {/* Context Chip */}
              <div className="mb-4 sm:mb-6 md:mb-8">
                <div className="inline-flex px-3 sm:px-4 py-2 border-1 border-gray-300 rounded-full text-gray-700 text-xs sm:text-sm font-medium">
                  Interface Design
                </div>
              </div>

              {/* Title */}
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 md:mb-8">
                AI Plan Finder Tool Interface
              </h2>

              <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed max-w-4xl">
                Here are the key interface components that make the AI Plan Finder Tool intuitive and efficient.
                Each component is designed to streamline the plan selection process and enhance user experience.
              </p>
            </div>

            {/* Interface Showcase Layout */}
            <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-12">
              {/* Left Side - Image */}
              <div className="lg:w-1/2 w-full order-first lg:order-last">
                <Image
                  src={ImgCozmoHome}
                  alt="Cozmo Plan Finder Tool Interface"
                  className="w-full h-auto rounded-lg"
                  priority
                />
              </div>

              {/* Right Side - Content */}
              <div className="lg:w-1/2 w-full order-last lg:order-first">
                <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-gray-900 mb-3 sm:mb-4 md:mb-6 leading-tight">
                  Find the perfect insurance plan with AI precision
                </h3>

                <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-400 leading-relaxed">
                  Browse thousands of insurance plans from multiple carriers with intelligent AI filtering.
                  Get personalized recommendations based on customer data, regional availability, and commission optimization.
                  Create your own plan comparisons or let our AI find the best matches for your clients.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Filtering Capabilities Section */}
        <section className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6 sm:mb-8 md:mb-12">
              <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-gray-900 mb-3 sm:mb-4 md:mb-6 leading-tight">
                Powerful Filtering for Precision Results
              </h3>

              {/* Filter Image */}
              <div className="mb-4 sm:mb-6 md:mb-8">
                <Image
                  src={ImgCozmoScreenshotFilter}
                  alt="Plan Finder Filtering Interface"
                  className="w-full h-auto"
                  priority
                />
              </div>

              <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
                Navigate through thousands of insurance plans with our intuitive filtering system.
                From basic plan types to specific financial criteria, every filter is designed to help you
                find the perfect match for your needs and budget.
              </p>
            </div>

            {/* Filter Categories */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {/* Plan Type Filters */}
              <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md border border-gray-100">
                <div className="flex items-center mb-2 sm:mb-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <h4 className="text-xs sm:text-sm md:text-base font-semibold text-gray-900">Plan Type</h4>
                </div>
                <p className="text-xs sm:text-sm text-gray-600">
                  Filter by Health, Dental, Vision, or comprehensive coverage options
                </p>
              </div>

              {/* Financial Filters */}
              <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md border border-gray-100">
                <div className="flex items-center mb-2 sm:mb-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-green-100 rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <h4 className="text-xs sm:text-sm md:text-base font-semibold text-gray-900">Financial Criteria</h4>
                </div>
                <p className="text-xs sm:text-sm text-gray-600">
                  Set income levels, deductibles, and out-of-pocket maximums
                </p>
              </div>

              {/* Carrier Selection */}
              <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md border border-gray-100">
                <div className="flex items-center mb-2 sm:mb-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h4 className="text-xs sm:text-sm md:text-base font-semibold text-gray-900">Carrier Options</h4>
                </div>
                <p className="text-xs sm:text-sm text-gray-600">
                  Choose from multiple carriers with real-time availability
                </p>
              </div>

              {/* AI Recommendations */}
              <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md border border-gray-100">
                <div className="flex items-center mb-2 sm:mb-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h4 className="text-xs sm:text-sm md:text-base font-semibold text-gray-900">AI Recommendations</h4>
                </div>
                <p className="text-xs sm:text-sm text-gray-600">
                  Get personalized suggestions based on your profile and preferences
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Lead Management Section */}
        <section className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-12">
              {/* Right Side - Content */}
              <div className="lg:w-1/2 w-full order-last lg:order-first">
                <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-gray-900 mb-3 sm:mb-4 md:mb-6 leading-tight">
                  Comprehensive Lead Management with Customer Data
                </h3>

                <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-400 leading-relaxed">
                  Streamline your lead management process with our comprehensive customer data collection and update system.
                  Capture detailed information including personal details, financial data, and policy preferences to ensure
                  accurate plan matching and efficient customer service.
                </p>
              </div>
              <div className="lg:w-1/2 w-full order-first lg:order-last">
                <Image
                  src={ImgCozmoUpdateLead}
                  alt="Lead Management Interface"
                  className="w-full h-auto rounded-lg"
                  priority
                />
              </div>
            </div>
          </div>
        </section>

        {/* Policy Management Section */}
        <section className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6 sm:mb-8 md:mb-12">
              <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-gray-900 mb-3 sm:mb-4 md:mb-6 leading-tight">
                Streamlined Policy Management & Submission
              </h3>

              <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
                Once you've found the perfect plan, our system makes it easy to manage policy details and submit everything to your CRM.
                The interface provides comprehensive forms for policy information, marketplace details, and billing information - all in one place.
              </p>
            </div>

            {/* Two Image Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
              {/* Left Image - Detailed Policy Form */}
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <Image
                  src={ImgCozmoSubmitPolicy1}
                  alt="Detailed Policy Management Form"
                  className="w-full h-auto"
                  priority
                />
                <div className="text-center">
                  <h4 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">Comprehensive Policy Details</h4>
                  <p className="text-xs sm:text-sm text-gray-600">
                    Manage all policy information including coverage details, pricing, effective dates, and billing information in one organized form.
                  </p>
                </div>
              </div>

              {/* Right Image - Policy Summary */}
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <Image
                  src={ImgCozmoSubmitPolicy2}
                  alt="Policy Summary and Submission"
                  className="w-full h-auto"
                  priority
                />
                <div className="text-center">
                  <h4 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">Quick Policy Summary</h4>
                  <p className="text-xs sm:text-sm text-gray-600">
                    Review policy information at a glance and submit directly to your CRM system with just one click.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Communication: The Secret Sauce to Our Success */}
        <section className="relative py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="container mx-auto px-4 max-w-6xl relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-start">
              {/* Left column - Title, Description, and Points */}
              <div>
                {/* Title and Description */}
                <div className="mb-8 sm:mb-10 md:mb-12">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                    Communication: The Secret Sauce to Our Success
                  </h2>
                  <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl">
                    What made the Cozmo Plan Finder Tool project shine? Our relentless commitment to transparent communication that kept everyone aligned and excited throughout the journey.
                  </p>
                </div>

                {/* Points */}
                <div className="space-y-6 sm:space-y-8">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-blue-600 flex items-center justify-center text-white mr-3 sm:mr-4 md:mr-5">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6">
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">No Surprises, Just Progress</h3>
                      <p className="text-xs sm:text-sm md:text-base text-gray-600">
                        Daily updates via your preferred platform kept everyone informed, eliminating the anxiety of wondering "what's happening with my app today?"
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-blue-700 flex items-center justify-center text-white mr-3 sm:mr-4 md:mr-5">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">Weekly Deep Dives That Matter</h3>
                      <p className="text-xs sm:text-sm md:text-base text-gray-600">
                        Our focused weekly meetings weren't just check-ins—they were collaborative strategy sessions that kept your vision at the center while driving momentum.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-blue-600 flex items-center justify-center text-white mr-3 sm:mr-4 md:mr-5">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6">
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">Problems Solved Before They Grew</h3>
                      <p className="text-xs sm:text-sm md:text-base text-gray-600">
                        Our team's proactive approach to challenges meant issues were identified and resolved early—keeping the project on track and your stress levels low.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-blue-700 flex items-center justify-center text-white mr-3 sm:mr-4 md:mr-5">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="3" y1="9" x2="21" y2="9"></line>
                        <line x1="9" y1="21" x2="9" y2="9"></line>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">Full Transparency, Zero Guesswork</h3>
                      <p className="text-xs sm:text-sm md:text-base text-gray-600">
                        Our project tracking system gave you real-time visibility into every milestone, eliminating the black-box development experience that plagues most projects.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side - Trello Card & Status Updates */}
              <div className="relative space-y-2">
                <div className="scale-75 mt-[-30px] overflow-hidden">
                  <Image
                    src={TrelloCard}
                    alt="Daily Status Updates Dashboard"
                    width={300}
                    height={100}
                    className="w-auto h-auto object-cover"
                  />
                </div>
                {/* Daily Status Updates Image */}
                <div className="rounded-2xl scale-90 mt-4 overflow-hidden">
                  <Image
                    src={DailyStatusUpdates}
                    alt="Daily Status Updates Dashboard"
                    width={600}
                    height={400}
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <div className="py-8 sm:py-12 md:py-16 lg:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="hidden lg:block w-full">
              <div className="relative bg-[#001F8F] overflow-hidden flex justify-center items-center rounded-[20px] py-8 sm:py-12 xl:py-16 px-4 sm:px-12 text-center lg:min-h-108">
                {/* Vector Background Image */}
                <Image
                  src={BannerBgVector}
                  alt="Contact Us"
                  className="absolute hidden lg:block -left-20 xl:left-26 2xl:left-46 w-fit object-contain z-0"
                />
                {/* Content */}
                <div className="relative z-10 flex flex-col justify-center items-center text-white gap-3 sm:gap-4 max-w-4xl">
                  <Image
                    src={LogoDvekrestWhite}
                    alt="Logo"
                    className="w-auto h-8 sm:h-10 md:h-12 mb-2"
                  />
                  <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-medium leading-snug">
                    Need a Custom CRM Solution?
                  </h1>
                  <p className="text-xs sm:text-sm md:text-base font-medium opacity-70 mb-2">
                    Let's build a tailored CRM system that perfectly fits your business requirements. From lead management to customer insights, we'll create the solution you need.
                  </p>
                  <Link href="/contact-us">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-custom-blue text-xs sm:text-sm md:text-base rounded-full px-4 sm:px-6 h-10 sm:h-12"
                    >
                      Book Free Consultation
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
            <div className="lg:hidden w-full">
              <div className="relative overflow-hidden flex bg-[#001F8F] justify-center items-center py-6 sm:py-8 md:py-12 text-center px-4 sm:px-6 rounded-[20px]">
                <Image
                  src={BannerBgVector}
                  alt="Contact Us"
                  className="absolute inset-0 w-full h-full object-cover z-0"
                />
                {/* Content */}
                <div className="relative z-10 flex flex-col justify-center items-center text-white gap-2 sm:gap-3 md:gap-4 max-w-3xl">
                  <Image
                    src={LogoDvekrestWhite}
                    alt="Logo"
                    className="w-auto h-6 sm:h-8 md:h-10 lg:h-12 mb-2"
                  />
                  <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium leading-snug">
                    Need a Custom CRM Solution?
                  </h1>
                  <p className="text-xs sm:text-sm md:text-base font-medium opacity-70 mb-2 text-center">
                    Let's build a tailored CRM system that perfectly fits your business requirements. From lead management to customer insights, we'll create the solution you need.
                  </p>
                  <Link href="/contact-us">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="font-semibold rounded-full text-xs sm:text-sm md:text-base"
                    >
                      Book Free Consultation
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default CozmoPlanFinderToolCaseStudy;
